import React, { useCallback, useRef, useState } from "react";
import { FaUpload } from "../../utils/icons";
import { collectFiles, readEntries } from "../../utils/folderFiles";

/**
 * Idle state (LU-A10, worklist-shaped redesign): full-width drag-and-drop zone
 * with a folder/file picker, living in the right pane. Purely presentational —
 * it normalizes a drop / picker selection into `File[]` and hands it up via
 * `onFiles`; parse / validate / PHI all live in the logic lane (the page).
 *
 * Flat house style: 1px dashed border, 2px radius, no shadow, mono micro labels.
 * The recursive folder-walk lives in `../../utils/folderFiles` (shared with the
 * batch FolderDropzone).
 */
interface DropzonePanelProps {
  onFiles: (files: File[]) => void;
  maxMb?: number;
}

export const DropzonePanel: React.FC<DropzonePanelProps> = ({ onFiles, maxMb = 500 }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const openPicker = useCallback(() => inputRef.current?.click(), []);
  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      // Capture entries + the flat file list SYNCHRONOUSLY — `dataTransfer` and
      // its entries are only valid during the drop event, not after an await.
      const dt = e.dataTransfer;
      const entries = readEntries(dt);
      const flat = Array.from(dt.files ?? []);
      void (async () => {
        // Folder drops only expose readable files via the entry API; fall back to
        // the flat list (multi-file, non-folder drops) when there are no entries.
        const walked = entries.length ? await collectFiles(entries) : [];
        const files = walked.length ? walked : flat;
        if (files.length > 0) onFiles(files);
      })();
    },
    [onFiles],
  );

  return (
    <div>
      <div
        data-testid="upload-dropzone"
        role="button"
        tabIndex={0}
        aria-label="Drag and drop a DICOM study folder, or click to browse"
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={[
          "group flex cursor-pointer flex-col items-center justify-center rounded border border-dashed px-7 py-24 text-center transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-signal-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
          dragging
            ? "border-signal bg-signal-tint"
            : "border-border-strong bg-signal-ambient hover:border-signal-stroke-soft",
        ].join(" ")}
      >
        <div
          className={[
            "mb-4 grid h-11 w-11 place-items-center rounded border transition-colors",
            dragging
              ? "border-signal text-signal"
              : "border-border-strong text-signal group-hover:border-signal-stroke-soft",
          ].join(" ")}
        >
          <FaUpload className="h-5 w-5" />
        </div>
        <p className="text-[15px] font-medium text-fg">
          {dragging ? (
            "Release to load study"
          ) : (
            <>
              Drop a study here, or{" "}
              <span className="font-semibold text-signal-text-tint">browse files</span>
            </>
          )}
        </p>
        <p className="mt-1.5 text-[13px] text-fg/55">
          A folder of DICOM images, or a set of <span className="font-mono">.dcm</span> files
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-6 font-mono text-[10px] tracking-[0.06em] text-fg/25">
          <span>CT ONLY</span>
          <span>ONE STUDY</span>
          <span>≤ {maxMb} MB</span>
        </div>

        <input
          ref={inputRef}
          type="file"
          multiple
          className="sr-only"
          data-testid="upload-file-input"
          onChange={(e) => {
            onFiles(Array.from(e.target.files ?? []));
            // Reset so re-picking the same folder after an error fires onChange.
            e.target.value = "";
          }}
          // webkitdirectory/directory enable folder selection; not in React's
          // input typings, so spread them as a plain attribute bag.
          {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
        />
      </div>
    </div>
  );
};
